import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import SwiperCore, { Keyboard, Navigation, Virtual } from 'swiper'
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react'
import { IVerticalBlock, IVerticalBlockItem } from '../../../types'
import ComponentTitle from '../../ComponentTitle/ComponentTitle'
import { Item } from './Item'
import { NavButton } from './NavButton'
import { PlayerProvider } from './PlayerContext'
import { loadPlayer, PlayerView } from './PlayerView'
import { useNearViewport } from './useNearViewport'
import { ReelsContainer, ReelsSwiperWrap } from './Reels.style'
import { PlayerContext } from '../../Player/Store/PlayerContext'
import { getParameterByName } from '../../../utils'
import { useGridBlock } from '../../../contexts'
import { useAdBlockStore } from '../../../store'
import Head from 'next/head'
import { he } from '../../../locales/he'
import { VerticalAdBlockSlide } from './VerticalAdBlockSlide'
import { reelsHe } from '../../../locales/reels.he'

function moveItem(array: IVerticalBlockItem[], fromIndex: number) {
  const front = array.slice(fromIndex)
  const back = array.slice(0, fromIndex)
  return {
    items: [...front, ...back],
    stagger: back.length,
  }
}

const OgHeadTags = ({ data }: { data: IVerticalBlockItem | null }) => {
  const { title: gridTitle } = useGridBlock()
  const { title, image, description } = useMemo(() => {
    if (!data) {
      return {
        title: null,
        image: null,
      }
    }
    return {
      title: [
        gridTitle,
        ', ',
        he.episode + ' ' + data.episode,
        ' | ',
        he.site_name,
      ].join(''),
      image: data.poster,
      description: data.title,
    }
  }, [data, gridTitle])

  if (!data || !title) {
    return null
  }

  return (
    <Head>
      <meta key={'og-title'} property='og:title' content={title} />
      <meta
        key={'og-description'}
        property='og:description'
        content={description}
      />
      <meta key={'og-image'} property='og:image' content={image} />
      <meta key={'og-url'} property='og:url' content={window.location.href} />
    </Head>
  )
}

/**
 * Reels component
 *
 * Chore: prefetch reels video before loading the component, to avoid kaltura loader
 *
 */
const Reels = ({ data }: { data: IVerticalBlock }) => {
  const { gridId } = useGridBlock()
  const [overideOG, setOverideOG] = useState<null | IVerticalBlockItem>(null)
  const {
    grid_title,
    posts,
    isMiniSeries,
    isBigBrother,
    adsInterval,
    startMuted = false,
  } = data
  const [swiperProps, setSwiperProps] = useState<null | SwiperProps>(null)
  const [showPlayer, setShowPlayer] = useState<boolean>(false)
  const updatedPost = useRef<IVerticalBlockItem[] | null>(null)
  const [reels, setReels] = useState<IVerticalBlockItem[]>(
    posts.map((p, index) => {
      p.originalIndex = index
      return p
    })
  )
  const [activeIndex, setActiveIndex] = useState(0)
  const { setVideoPlaying } = useContext(PlayerContext)

  const swiperRef = useRef<SwiperCore | null>(null)
  const containerRef = useRef(null)
  const clickedVideo = useRef<number>(0)
  const clickedOriginalIndex = useRef<number>(0)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const adDecision = useAdBlockStore(state => state.decision)
  const anchorHandledRef = useRef(false)

  // Like a lazy image: the cards' posters, the preview clip and the player's
  // code load only once the section comes near the screen (`seen`), and the
  // preview plays only while it is there (`inView`)
  const view = useNearViewport(wrapperRef)
  useEffect(() => {
    if (view.seen) {
      loadPlayer()
    }
  }, [view.seen])

  const handleSlideChange = useCallback(
    (swiper: SwiperCore) => {
      const currentMiddle = Math.floor(reels.length / 2)
      setActiveIndex(swiper.realIndex)

      const slidesPerView =
        (swiper.params.slidesPerView !== 'auto' &&
          swiper.params.slidesPerView) ||
        2

      if (
        swiper.realIndex >= currentMiddle ||
        reels.length < slidesPerView * 2
      ) {
        setReels(prev => [...prev, ...posts])
      }
    },
    [posts, reels.length]
  )

  useEffect(() => {
    setSwiperProps({
      modules: [Keyboard, Navigation, Virtual],
      spaceBetween: 16,
      watchSlidesProgress: true,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      virtual: {
        enabled: true,
        slides: reels,
        addSlidesBefore: 6,
        addSlidesAfter: 6,
      },
      breakpoints: {
        // Large desktop (> 1280px)
        1280: {
          slidesPerView: 5.9,
          slidesPerGroup: 5,
        },
        // Desktop/tablet (> 768px)
        768: {
          slidesPerView: 3.9,
          slidesPerGroup: 3,
        },
        480: {
          slidesPerView: 2.9,
          slidesPerGroup: 2,
        },
        // Mobile (≤ 768px)
        0: {
          slidesPerView: 1.2,
          slidesPerGroup: 1,
        },
      },
      onSlideChange: handleSlideChange,
      onSwiper: swiper => (swiperRef.current = swiper),
    })
  }, [handleSlideChange, reels])

  // Lock body scroll when the modal is open
  useEffect(() => {
    if (showPlayer) {
      document.body.style.overflow = 'hidden' // Disable scrolling
    } else {
      document.body.style.overflow = 'auto' // Enable scrolling when modal is closed
    }

    // Clean up on unmount
    return () => {
      document.body.style.overflow = 'auto' // Ensure scrolling is re-enabled
    }
  }, [showPlayer])

  const handleSlideClick = useCallback(
    (i: number) => {
      const { items, stagger } = moveItem(reels, i)
      clickedVideo.current = stagger
      clickedOriginalIndex.current = i
      updatedPost.current = items
      setShowPlayer(true)
      setVideoPlaying(true)
    },
    [reels, setVideoPlaying]
  )
  const handleClosePlayer = (index: number) => {
    setShowPlayer(false)
    setVideoPlaying(false)
    swiperRef.current?.slideTo(index)
    clickedVideo.current = 0
  }

  useEffect(() => {
    if (anchorHandledRef.current) {
      return
    }

    const anchorParams = getParameterByName('vpanchor')

    if (!anchorParams) {
      return
    }

    const [grid, indexStr] = anchorParams.split('-')

    if (grid != gridId) {
      return
    }

    const index = parseInt(indexStr, 10)

    if (isNaN(index) || index < 0 || index >= posts.length) {
      return
    }

    let cancelled = false
    let retryTimeout: ReturnType<typeof setTimeout>

    const tryOpen = () => {
      if (cancelled) {
        return
      }
      if (!window.KalturaPlayer) {
        retryTimeout = setTimeout(tryOpen, 100)
        return
      }

      // Deep-link auto-open must run exactly once: `reels` (a dependency of
      // this effect) is mutated by unrelated interactions — most notably the
      // swiperRef.slideTo() call in handleClosePlayer, which fires the grid's
      // onSlideChange and can append to `reels`. Mark handled and strip the
      // URL only once the player is actually about to open, so an
      // interrupted retry (e.g. the effect re-running before KalturaPlayer
      // is ready) can't lose the anchor without ever opening the player.
      anchorHandledRef.current = true
      const url = new URL(window.location.href)
      url.searchParams.delete('vpanchor')
      window.history.replaceState(null, '', url.toString())

      wrapperRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
      setOverideOG(reels[index])
      handleSlideClick(index)
    }
    tryOpen()

    return () => {
      cancelled = true
      clearTimeout(retryTimeout)
    }
  }, [gridId, handleSlideClick, posts.length, reels])

  return (
    <PlayerProvider
      isMiniSeries={isMiniSeries}
      isBigBrother={isBigBrother}
      adsInterval={adsInterval}
      startMuted={startMuted}
    >
      <OgHeadTags data={overideOG} />
      <ReelsContainer key={'ReelsContainer'} ref={wrapperRef}>
        {grid_title && <ComponentTitle componentTitle={grid_title} />}
        <ReelsSwiperWrap ref={containerRef}>
          {swiperProps && (
            <Swiper key={'virtual'} {...swiperProps}>
              {reels.map(
                (data, index) =>
                  data.poster && (
                    <SwiperSlide
                      key={index}
                      className='reels-slide'
                      role='button'
                      tabIndex={0}
                      aria-label={data.title || reelsHe.play}
                      onClick={() => handleSlideClick(index)}
                      onKeyDown={(e: {
                        key: string
                        preventDefault: () => void
                      }) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleSlideClick(index)
                        }
                      }}
                      virtualIndex={index}
                    >
                      {/* no preview under the open player */}
                      <Item
                        data={data}
                        playing={
                          !showPlayer && view.inView && index === activeIndex
                        }
                        showPoster={view.seen}
                      />
                    </SwiperSlide>
                  )
              )}
              <NavButton />
            </Swiper>
          )}
        </ReelsSwiperWrap>
      </ReelsContainer>
      {showPlayer && updatedPost.current && adDecision === 'block' && (
        <VerticalAdBlockSlide
          onClose={() => handleClosePlayer(clickedOriginalIndex.current)}
        />
      )}
      {showPlayer && updatedPost.current && adDecision !== 'block' && (
        <PlayerView
          showPlayer={showPlayer}
          stagger={clickedVideo.current}
          closePlayer={handleClosePlayer}
          updatedPost={isMiniSeries ? reels : updatedPost.current}
          loop={!isMiniSeries}
        />
      )}
    </PlayerProvider>
  )
}

export default Reels
